import React from "react";
import moment from "moment";
import {
    EuiFlyout,
    EuiFlyoutHeader,
    EuiFlyoutBody,
    EuiDescriptionList,
    EuiDescriptionListDescription,
    EuiTitle,
    EuiDescriptionListTitle,
    EuiFlexGroup,
    EuiFlexItem,
    EuiButtonEmpty,
    EuiImage,
    EuiSpacer,
    EuiEmptyPrompt,
    EuiLoadingContent,
    EuiCallOut,
    EuiListGroup,
    EuiListGroupItem,
    EuiIcon,
} from "@elastic/eui";
import _ from "lodash";

const renderTextPreviewField = (label, value) => (
    <>
        <EuiDescriptionListTitle>{label}</EuiDescriptionListTitle>
        <EuiDescriptionListDescription>{`${value} `}</EuiDescriptionListDescription>
    </>
);

const renderImagePreviewField = (label, url, isDownloadable) => (
    <>
        <EuiDescriptionListTitle>{label}</EuiDescriptionListTitle>
        <EuiDescriptionListDescription>
            <EuiFlexItem grow={false}>
                <EuiImage
                    size="m"
                    hasShadow
                    allowFullScreen
                    caption={label}
                    url={url}
                />
                {isDownloadable && (
                    <EuiButtonEmpty
                        href={url}
                        download={label}
                    >
                        Download {label}
                    </EuiButtonEmpty>
                )}
            </EuiFlexItem>
        </EuiDescriptionListDescription>
    </>
);

const renderListIncludedFields = ({ label, hasPreviewPage, entityName }, fields) => (
    <>
        <EuiDescriptionListTitle>{label}</EuiDescriptionListTitle>
        <EuiDescriptionListDescription>
            <EuiListGroup
                flush
                bordered
            >
                {fields.map((field) => (
                    <EuiListGroupItem
                        label={(hasPreviewPage ? "#" : "") + field.label}
                        icon={
                            <EuiIcon
                                type="dot"
                                color="#808080"
                            />
                        }
                        href={hasPreviewPage ? `${entityName}/${field.value}` : ""}
                    />
                ))}
            </EuiListGroup>
        </EuiDescriptionListDescription>
    </>
);

const mapEntitiesToOptions = (entities, label) => {
    if (_.isNil(entities)) {
        return null;
    }

    return entities.map((entity) => ({
        label: entity[label].includes("@") ? entity[label] : _.startCase(entity[label]),
        value: entity.id,
    }));
};

export default ({
    entityObject,
    options,
    includedEntities,
    isLoading,
    errors,
    onCancel,
    header,
}) => {
    const renderError = () => {
        if (!errors) {
            return;
        }
        return (
            <>
                <EuiCallOut
                    iconType="faceSad"
                    color="danger"
                    title={`${errors}`}
                />
                <EuiSpacer size="l" />
            </>
        );
    };

    const renderLoadingText = () => {
        if (!isLoading) {
            return;
        } else {
            return (
                <>
                    <EuiLoadingContent lines={3} />
                </>
            );
        }
    };

    if (errors) {
        return renderError();
    } else {
        return options.previewFields ? (
            <EuiFlyout
                maxWidth={600}
                ownFocus
                onClose={onCancel}
                aria-labelledby="flyoutLargeTitle"
            >
                <EuiFlyoutHeader hasBorder>
                    <EuiTitle size="m">
                        <h2 id="flyoutLargeTitle">{header}</h2>
                    </EuiTitle>
                </EuiFlyoutHeader>
                <EuiFlyoutBody>
                    <EuiDescriptionList>
                        {options.previewFields.map((field) => {
                            switch (field.type) {
                                case "image":
                                    return (
                                        entityObject[field.name]?.url &&
                                        renderImagePreviewField(
                                            field.label,
                                            entityObject[field.name]?.url,
                                            field.isDownloadable
                                        )
                                    );
                                case "base64image":
                                    return (
                                        entityObject[field.name] &&
                                        renderImagePreviewField(
                                            field.label,
                                            `data:image/png;base64,${entityObject[field.name]}`,
                                            field.isDownloadable
                                        )
                                    );
                                case "relation":
                                    break;
                                case "date":
                                    return renderTextPreviewField(
                                        field.label,
                                        (entityObject[field.name] &&
                                            (moment(entityObject[field.name]).format("LLL") ||
                                                "N/A")) ||
                                            "N/A"
                                    );
                                case "list":
                                    if (
                                        options.entityName === "organizationAssignees" &&
                                        field.label === "branches"
                                    ) {
                                        return (
                                            entityObject?.branches.length > 0 &&
                                            renderListIncludedFields(field, entityObject.branches)
                                        );
                                    }
                                    const includedFields = mapEntitiesToOptions(
                                        includedEntities[field.entityName],
                                        field.filterValue
                                    );
                                    return (
                                        field.include &&
                                        includedFields &&
                                        includedFields.length > 0 &&
                                        renderListIncludedFields(field, includedFields)
                                    );
                                default:
                                    return renderTextPreviewField(
                                        field.label,
                                        entityObject[field.name] || "N/A"
                                    );
                            }
                        })}
                    </EuiDescriptionList>
                    {renderLoadingText()}
                    <EuiSpacer size={"xl"} />
                    <EuiFlexGroup justifyContent={"flexEnd"}>
                        <EuiFlexItem grow={false}>
                            <EuiButtonEmpty onClick={onCancel}>Cancel</EuiButtonEmpty>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiFlyoutBody>
            </EuiFlyout>
        ) : (
            <EuiEmptyPrompt
                iconType="editorStrike"
                title={<h2>Nothing to show</h2>}
                body={<p>No details to preview</p>}
            />
        );
    }
};
